import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabase';

interface DownloadStats {
  total_downloads: number;
  top_plugins: Array<{
    plugin_id: string;
    download_count: number;
    unique_users: number;
  }>;
  recent_downloads: Array<{
    plugin_id: string;
    user_id?: string;
    username?: string;
    created_at: string;
    download_type: string;
  }>;
  downloads_by_day: Array<{
    date: string;
    downloads: number;
  }>;
}

export const AdminStatistics: React.FC = () => {
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState<DownloadStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAdmin) {
      loadStatistics();
    }
  }, [isAdmin]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to get basic statistics from existing tables
      const { data: downloadsData, error: downloadsError } = await supabase
        .from('plugin_downloads')
        .select('plugin_id, created_at, download_type, user_id')
        .order('created_at', { ascending: false })
        .limit(1000);

      if (downloadsError) {
        console.error('Downloads query error:', downloadsError);
        setError('Failed to load download statistics');
        return;
      }

      // Process the data to create statistics
      const totalDownloads = downloadsData?.length || 0;
      const uniqueUsers = new Set(downloadsData?.map(d => d.user_id).filter(Boolean)).size;
      
      // Group by plugin
      const pluginStats: { [key: string]: number } = {};
      downloadsData?.forEach(download => {
        pluginStats[download.plugin_id] = (pluginStats[download.plugin_id] || 0) + 1;
      });

      const topPlugins = Object.entries(pluginStats)
        .map(([plugin_id, download_count]) => ({
          plugin_id,
          download_count,
          unique_users: new Set(downloadsData?.filter(d => d.plugin_id === plugin_id && d.user_id).map(d => d.user_id)).size
        }))
        .sort((a, b) => b.download_count - a.download_count)
        .slice(0, 10);

      // Recent downloads
      const recentDownloads = (downloadsData || []).slice(0, 20).map(download => ({
        plugin_id: download.plugin_id,
        user_id: download.user_id,
        username: download.user_id ? 'User' : 'Anonymous',
        created_at: download.created_at,
        download_type: download.download_type
      }));

      // Downloads by day (last 30 days)
      const now = new Date();
      const downloadsByDay = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        const dayDownloads = downloadsData?.filter(d => 
          d.created_at.split('T')[0] === dateStr
        ).length || 0;
        
        downloadsByDay.push({
          date: dateStr,
          downloads: dayDownloads
        });
      }

      setStats({
        total_downloads: totalDownloads,
        top_plugins: topPlugins,
        recent_downloads: recentDownloads,
        downloads_by_day: downloadsByDay
      });

    } catch (err) {
      console.error('Error loading statistics:', err);
      setError('Failed to load statistics. This might be normal if no downloads have been recorded yet.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-100 border border-red-300 rounded-lg p-4 text-red-700">
          Access denied. Admin privileges required.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div>
          <p className="mt-2 text-theme-text">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-100 border border-red-300 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-theme-text">Download Statistics</h1>
        <button
          onClick={loadStatistics}
          className="px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-theme-secondary transition-colors"
        >
          🔄 Refresh
        </button>
      </div>

      {stats && (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-theme-surface border border-theme-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-theme-text-secondary text-sm">Total Downloads</p>
                  <p className="text-2xl font-bold text-theme-text">{stats.total_downloads.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">
                  <img 
                    src="https://unpkg.com/pixelarticons@1.8.0/svg/download.svg" 
                    alt="Downloads" 
                    className="w-8 h-8 inline-block"
                    style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }}
                  />
                </span>
                </div>
              </div>
            </div>

            <div className="bg-theme-surface border border-theme-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-theme-text-secondary text-sm">Popular Plugins</p>
                  <p className="text-2xl font-bold text-theme-text">{stats.top_plugins.length}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">
                    <img 
                      src="https://unpkg.com/pixelarticons@1.8.0/svg/trending-up.svg" 
                      alt="Trending" 
                      className="w-8 h-8 inline-block"
                      style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }}
                    />
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-theme-surface border border-theme-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-theme-text-secondary text-sm">Total Unique Users</p>
                  <p className="text-2xl font-bold text-theme-text">
                    {stats.top_plugins.reduce((sum, plugin) => sum + plugin.unique_users, 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">👥</span>
                </div>
              </div>
            </div>

            <div className="bg-theme-surface border border-theme-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-theme-text-secondary text-sm">Avg per Plugin</p>
                  <p className="text-2xl font-bold text-theme-text">
                    {stats.top_plugins.length > 0 
                      ? Math.round(stats.total_downloads / stats.top_plugins.length)
                      : 0
                    }
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">
                  <img 
                    src="https://unpkg.com/pixelarticons@1.8.0/svg/chart-bar.svg" 
                    alt="Chart" 
                    className="w-8 h-8 inline-block"
                    style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }}
                  />
                </span>
                </div>
              </div>
            </div>
          </div>

          {/* Downloads Chart */}
          <div className="bg-theme-surface border border-theme-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-theme-text mb-4">Downloads Over Time (Last 30 Days)</h2>
            <div className="h-64 flex items-end justify-between gap-1">
              {stats.downloads_by_day.map((day, index) => {
                const maxDownloads = Math.max(...stats.downloads_by_day.map(d => d.downloads));
                const height = maxDownloads > 0 ? (day.downloads / maxDownloads) * 100 : 0;
                
                return (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-theme-primary rounded-t"
                      style={{ height: `${height}%`, minHeight: height > 0 ? '4px' : '0' }}
                      title={`${day.date}: ${day.downloads} downloads`}
                    ></div>
                    <span className="text-xs text-theme-text-secondary mt-1 transform -rotate-45 origin-left">
                      {new Date(day.date).getDate()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Plugins */}
            <div className="bg-theme-surface border border-theme-border rounded-lg p-6">
              <h2 className="text-xl font-bold text-theme-text mb-4">Most Downloaded Plugins</h2>
              <div className="space-y-3">
                {stats.top_plugins.slice(0, 10).map((plugin, index) => (
                  <div key={plugin.plugin_id} className="flex items-center justify-between p-3 bg-theme-bg rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-theme-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-theme-text">{plugin.plugin_id}</p>
                        <p className="text-sm text-theme-text-secondary">
                          {plugin.unique_users} unique users
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-theme-text">{plugin.download_count}</p>
                      <p className="text-sm text-theme-text-secondary">downloads</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Downloads */}
            <div className="bg-theme-surface border border-theme-border rounded-lg p-6">
              <h2 className="text-xl font-bold text-theme-text mb-4">Recent Downloads</h2>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {stats.recent_downloads.map((download, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-theme-bg rounded-lg">
                    <div>
                      <p className="font-medium text-theme-text">{download.plugin_id}</p>
                      <div className="flex items-center gap-2 text-sm text-theme-text-secondary">
                        <span>{download.username || 'Anonymous'}</span>
                        <span>•</span>
                        <span>{download.download_type}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-theme-text-secondary">
                        {formatDate(download.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Export Options */}
          <div className="bg-theme-surface border border-theme-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-theme-text mb-4">Export Data</h2>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  const dataStr = JSON.stringify(stats, null, 2);
                  const dataBlob = new Blob([dataStr], { type: 'application/json' });
                  const url = URL.createObjectURL(dataBlob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `plugin-statistics-${new Date().toISOString().split('T')[0]}.json`;
                  link.click();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <img 
                src="https://unpkg.com/pixelarticons@1.8.0/svg/file-export.svg" 
                alt="Export" 
                className="w-4 h-4 inline-block mr-2"
                style={{ filter: 'brightness(0) saturate(100%) invert(1)' }}
              />
              Export JSON
              </button>
              
              <button
                onClick={() => {
                  const csvData = [
                    ['Plugin ID', 'Downloads', 'Unique Users'],
                    ...stats.top_plugins.map(p => [p.plugin_id, p.download_count, p.unique_users])
                  ];
                  const csvStr = csvData.map(row => row.join(',')).join('\n');
                  const dataBlob = new Blob([csvStr], { type: 'text/csv' });
                  const url = URL.createObjectURL(dataBlob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `plugin-downloads-${new Date().toISOString().split('T')[0]}.csv`;
                  link.click();
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <img 
                src="https://unpkg.com/pixelarticons@1.8.0/svg/table.svg" 
                alt="CSV" 
                className="w-4 h-4 inline-block mr-2"
                style={{ filter: 'brightness(0) saturate(100%) invert(1)' }}
              />
              Export CSV
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}; 